import { useCallback, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { useCurrentSite } from '@hotjar/site';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import {
  readChartTypeFromUrl,
  getStepSeriesLabel,
  getDefaultFunnelQuery,
  getSeriesLabel,
} from '@hotjar/dashboard-quantz';
import type { FunnelQuery } from '@hotjar/dashboard-quantz';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { PageLoader } from '@hotjar/webapp/components/PageLoader/PageLoader';
import { getColorByIndex } from '@hotjar/charts';
import {
  convertFromFilterTypeToQueryFormat,
  convertFromNewUiToQueryFormat,
  readDateRangeFromUrl,
  readFilterSeriesFromUrl,
  readStepsFromUrl,
} from '@hotjar/global-filters';
import { useTranslation } from '@hotjar/translations';
import { FunnelsPageWithData } from './FunnelsPageWithData';

export const AsyncFunnelsPage = (): JSX.Element => {
  const { t } = useTranslation('funnels');
  usePageTitle(`${t('FunnelsPage.title')} - Hotjar`);
  const { data: site } = useCurrentSite();
  const { isLoading, coveragePercentage } = useTrafficCoverage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();
  const [currentFunnelQuery, setCurrentFunnelQuery] = useState<FunnelQuery>(
    getDefaultFunnelQuery(),
  );
  const [urlHasBeenRead, setUrlHasBeenRead] = useState<boolean>(false);

  const setCurrentFunnelQueryAndUrl = useCallback(
    (newFunnelQuery: FunnelQuery) => {
      setCurrentFunnelQuery(newFunnelQuery);

      const keyValuePairList: [string, string][] = [];
      // Steps part
      const stepsString = JSON.stringify(
        newFunnelQuery.steps.map((item) => convertFromFilterTypeToQueryFormat(item.stepInUiFormat)),
      );
      keyValuePairList.push(['steps', stepsString]);

      // Global filter part
      const globalFilterString = JSON.stringify(
        newFunnelQuery.filterChainSeries.map((item) =>
          convertFromFilterTypeToQueryFormat(item.filtersInUiFormat),
        ),
      );
      keyValuePairList.push(['filter-series', globalFilterString]);

      // dateRange part
      const dateRangeString = JSON.stringify(
        convertFromNewUiToQueryFormat({
          dateRange: newFunnelQuery.dateRange,
          filterList: [],
        }),
      );
      keyValuePairList.push(['date', dateRangeString]);

      // chart type part
      keyValuePairList.push(['chart', newFunnelQuery.chartType]);

      setSearchParams(keyValuePairList);
    },
    [setCurrentFunnelQuery, setSearchParams],
  );

  const setFunnelQueryFromUrl = useCallback(() => {
    let funnelsQueryFromUrl: FunnelQuery;
    try {
      const dateRangeResult = readDateRangeFromUrl();
      const stepsResult = readStepsFromUrl();
      const globalFilterSeriesResult = readFilterSeriesFromUrl();
      if (
        stepsResult.stepsList.length < 2 ||
        globalFilterSeriesResult.filterSeriesInUiFormat.length === 0
      ) {
        funnelsQueryFromUrl = getDefaultFunnelQuery();
      } else {
        funnelsQueryFromUrl = {
          dateRange: dateRangeResult.dateRange,
          steps: stepsResult.stepsList.map((item) => ({
            key: uuid(),
            stepInUiFormat: item,
            label: getStepSeriesLabel(item),
          })),
          filterChainSeries: globalFilterSeriesResult.filterSeriesInUiFormat.map((item, index) => ({
            key: uuid(),
            filtersInUiFormat: item,
            color: getColorByIndex(index),
            label: getSeriesLabel(item),
          })),
          chartType: readChartTypeFromUrl(),
        };
      }
    } catch {
      funnelsQueryFromUrl = getDefaultFunnelQuery();
    }
    setCurrentFunnelQuery(funnelsQueryFromUrl);
    setUrlHasBeenRead(true);
  }, []);

  useEffect(() => {
    setFunnelQueryFromUrl();
    // We just want to run it once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!site || isLoading || !urlHasBeenRead) {
    return <PageLoader />;
  }

  return (
    <FunnelsPageWithData
      site={site}
      coveragePercentage={coveragePercentage}
      currentFunnelQuery={currentFunnelQuery}
      setCurrentFunnelQueryAndUrl={setCurrentFunnelQueryAndUrl}
    />
  );
};
